import React, { useState } from 'react';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Checkbox } from '@progress/kendo-react-inputs';
import IconPicker from '../../../Shared/IconPicker/IconPicker';
import { useAttributeFormBuilder } from '../../../../Hooks/Display/useComponentProperties';
import { useTranslation } from 'react-i18next';
import ErrorMessage from '../../../Shared/Error/ErrorMessage';
import GenericTranslationDialog from '../../../Entities/GenericTranslationDialog';
import { usePropsStoreValue } from '../../../../Hooks/PropStore/usePropsStore';
import { useContext } from 'react';
import { GlobalContext } from '../../../../Renderers/GlobalContext';

const EditFormActionDialog = ({ handleClose, actionId }) => {

    const { t } = useTranslation([
        "display/components/form"
    ]);
    const project = usePropsStoreValue("project");

    const { subscription } = useContext(GlobalContext).state

    const [{ formActions }, dispatch] = useAttributeFormBuilder();
    const [action, setAction] = useState(formActions.find(action => action.id === actionId) || {});
    const [validName, setValidName] = useState({
        isValid: true,
        errorMessage: ""
    });

    const [showLabelTranslationDialog, setShowLabelTranslationDialog] = useState(false);

    const handleSave = () => {
        dispatch.updateFormAction(action);
        handleClose();
    }

    const handleActionNameChange = (e) => {
        const name = e.target.value.trim();
        const nameIsBlank = name === "";
        const nameStartWithNumber = /^\d/.test(name);
        const isNameLong = name.length > 64;

        const validName = {
            isValid: !(nameIsBlank || nameStartWithNumber || isNameLong),
            errorMessage: ""
        }

        if (!validName.isValid) {
            if (nameIsBlank) {
                validName.errorMessage = t("form_action_dialog.errors.label_required", { ns: "display/components/form" });
            } else if (nameStartWithNumber) {
                validName.errorMessage = t("form_action_dialog.errors.Label_cannot_start_with_number", { ns: "display/components/form" });
            } else if (isNameLong) {
                validName.errorMessage = t("form_action_dialog.errors.label_cannot_be_too_long", { ns: "display/components/form" });
            } else {
                validName.errorMessage = "";
            }
        }
        setValidName(validName);
        setAction({
            ...action,
            name: e.target.value
        });
    }

    function setLabelTranslation(data) {
        setAction({
            ...action,
            languages: data
        });
        setShowLabelTranslationDialog(false);
    }

    const shouldSaveDisabled = () => {
        return !action.name || (action.isActionHasIcon && !action.icon) || !validName.isValid;
    }

    return (
        <Dialog
            title={t("form_action_dialog.title", { ns: "display/components/form" })}
            onClose={handleClose}
        >
            <div className="mb-3">
                <label className="form-label">
                    {t("form_action_dialog.action_field_label", { ns: "display/components/form" })}
                </label>
                <p className='text-muted'>
                    {t(`form_action_dialog.actions.${action.type.toLowerCase()}`, { ns: "display/components/form" })}
                </p>
            </div>
            <div className="mb-3">
                <label className="form-label" required>
                    {t("form_action_dialog.label_field_label", { ns: "display/components/form" })}
                </label>
                {!validName.isValid &&
                    <ErrorMessage
                        valid={validName.isValid}
                        error={validName.errorMessage}
                    />
                }
                <input
                    type="text"
                    className="form-control"
                    value={action.name}
                    onChange={handleActionNameChange}
                />
            </div>
            {subscription.numOfLanguages > 1 &&
                <div className="mb-3">
                    <div className="form-label d-inline-block cursor-pointer" onClick={() => {
                        setShowLabelTranslationDialog(true)
                    }}>
                        <span className="text-primary">
                            {t("form_labels.label_translations")}
                        </span>
                    </div>
                </div>
            }
            {showLabelTranslationDialog && (
                <GenericTranslationDialog
                    onClose={() => { setShowLabelTranslationDialog(false) }}
                    onSave={setLabelTranslation}
                    languages={action.languages}
                    project={project}
                    fields={["label"]}
                    type={t("form_action_dialog.action_field_label")}
                />
            )}
            <div className="mb-3">
                <Checkbox
                    checked={action.isActionHasIcon}
                    label={t("form_action_dialog.add_icon_label", { ns: "display/components/form" })}
                    labelPlacement={'after'}
                    onChange={(event) => setAction({
                        ...action,
                        icon: "",
                        isActionHasIcon: event.value
                    })}
                />
            </div>
            {
                action.isActionHasIcon &&
                <div className="mb-3">
                    <label className="form-check-label" for="exampleCheck1">
                        {t("form_action_dialog.icon_field_label", { ns: "display/components/form" })}
                    </label>
                    <IconPicker
                        disabled={!action.isActionHasIcon}
                        isActionIcon={true}
                        selected={action.icon}
                        onChange={(props, value) => setAction({
                            ...action,
                            icon: props,
                        })}
                    />
                </div>
            }
            <div className="d-flex justify-content-end">
                <button type="button" className="btn btn-secondary me-2" onClick={handleClose}>
                    {t("form_action_dialog.buttons.cancel", { ns: "display/components/form" })}
                </button>
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSave}
                    disabled={shouldSaveDisabled()}
                >
                    {t("form_action_dialog.buttons.save", { ns: "display/components/form" })}
                </button>
            </div>
        </Dialog>
    )
}

export default EditFormActionDialog;